import React, {
  useEffect,
  useState,
} from "react";

import {
  Link,
} from "react-router-dom";

import {
  getTodayEventsApi,
} from "../api/EventActions";

const Events: React.FC = () => {
  const [
    events,
    setEvents,
  ] = useState<any[]>([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  // ==========================
  // CURRENT USER
  // Guest user is also allowed.
  // ==========================
  const user = JSON.parse(
    localStorage.getItem(
      "user"
    ) || "{}"
  );

  const userId =
    user?.user_id || null;

  // ==========================
  // FETCH PROGRAM / EVENT FEED
  //
  // Guest:
  // GET /programs/feed
  //
  // Logged-in:
  // GET /programs/feed?user_id=43
  // ==========================
  const fetchEvents =
    async () => {
      try {
        setLoading(true);

        const result =
          await getTodayEventsApi(
            userId
          );

        if (
          result?.success ===
            false ||
          result?.status ===
            false
        ) {
          setEvents([]);
          return;
        }

        setEvents(
          Array.isArray(
            result?.data
          )
            ? result.data
            : []
        );
      } catch (error) {
        console.error(
          "Programs fetch error:",
          error
        );

        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

  // ==========================
  // LOAD EVENTS
  // ==========================
  useEffect(() => {
    fetchEvents();

    // eslint-disable-next-line
  }, [userId]);

  // ==========================
  // FORMAT PROGRAM DATE
  // ==========================
  const formatDate = (
    dateStr?: string | null
  ) => {
    if (!dateStr) {
      return {
        day: "",
        month: "",
      };
    }

    const date =
      new Date(dateStr);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return {
        day: "",
        month: "",
      };
    }

    return {
      day:
        date.getDate(),

      month:
        date
          .toLocaleString(
            "default",
            {
              month:
                "short",
            }
          )
          .toUpperCase(),
    };
  };

  // ==========================
  // FORMAT TIME
  // ==========================
  const formatTime = (
    time?: string | null
  ) => {
    if (!time) {
      return "";
    }

    const parts =
      String(time)
        .split(":");

    if (
      parts.length < 2
    ) {
      return time;
    }

    const hours =
      Number(parts[0]);

    const minutes =
      Number(parts[1]);

    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      return time;
    }

    const suffix =
      hours >= 12
        ? "PM"
        : "AM";

    const formattedHour =
      hours % 12 || 12;

    return `${formattedHour}:${String(
      minutes
    ).padStart(
      2,
      "0"
    )} ${suffix}`;
  };

  return (
    <div className="bs-events-card">
      {/* ==========================
          HEADER
      ========================== */}
      <div className="bs-events-header">
        <div className="bs-events-title-wrap">
          <div className="bs-events-title-icon">
            <i className="feather-calendar" />
          </div>

          <h4>
            Events
          </h4>
        </div>

        <Link
          to="/defaultevent"
          className="bs-events-seeall"
        >
          See all

          <i className="feather-arrow-right" />
        </Link>
      </div>

      {/* ==========================
          LOADING
      ========================== */}
      {loading && (
        <div className="bs-events-loading">
          {Array.from({
            length: 2,
          }).map(
            (_, index) => (
              <div
                key={index}
                className="bs-events-skeleton-row"
              >
                <div className="bs-events-skeleton-date" />

                <div className="bs-events-skeleton-content">
                  <div className="bs-events-skeleton-line bs-events-skeleton-line--title" />

                  <div className="bs-events-skeleton-line bs-events-skeleton-line--small" />
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* ==========================
          EMPTY STATE
      ========================== */}
      {!loading &&
        events.length ===
          0 && (
          <div className="bs-events-empty">
            <div className="bs-events-empty-icon">
              <i className="feather-calendar" />
            </div>

            <h5>
              No upcoming events
            </h5>

            <p>
              New programs and club
              events will appear here.
            </p>

            <Link
              to="/defaultevent"
              className="bs-events-explore"
            >
              Explore Events

              <i className="feather-arrow-right" />
            </Link>
          </div>
        )}

      {/* ==========================
          PROGRAM / EVENT LIST
      ========================== */}
      {!loading &&
        events.length >
          0 && (
          <div className="bs-events-list">
            {events
              .slice(0, 4)
              .map(
                (
                  event: any,
                  index: number
                ) => {
                  const {
                    day,
                    month,
                  } = formatDate(
                    event.start_date
                  );

                  const time =
                    formatTime(
                      event.start_time
                    );

                  const venue =
                    event.venue_name ||
                    event.venue_address ||
                    "Venue not specified";

                  const programUrl =
                    event.program_id
                      ? `/program/${event.program_id}`
                      : "/defaultevent";

                  return (
                    <Link
                      key={
                        event.program_id ||
                        index
                      }
                      to={programUrl}
                      className="bs-events-item"
                    >
                      {/* DATE */}
                      <div className="bs-events-date">
                        <span className="bs-events-month">
                          {month ||
                            "--"}
                        </span>

                        <strong>
                          {day ||
                            "--"}
                        </strong>
                      </div>

                      {/* DETAILS */}
                      <div className="bs-events-info">
                        <div className="bs-events-topline">
                          <h5>
                            {event.program_title ||
                              "Program"}
                          </h5>

                          {event.category_name && (
                            <span className="bs-events-category">
                              {
                                event.category_name
                              }
                            </span>
                          )}
                        </div>

                        <div className="bs-events-meta">
                          <span>
                            <i className="feather-map-pin" />

                            {venue}
                          </span>

                          {time && (
                            <span>
                              <i className="feather-clock" />

                              {time}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="bs-events-arrow">
                        <i className="feather-chevron-right" />
                      </div>
                    </Link>
                  );
                }
              )}
          </div>
        )}

      <style>{`
        /* ==========================================
           EVENTS CARD
        ========================================== */

        .bs-events-card {
          width: 100%;
          margin-bottom: 18px;

          border:
            1px solid #E5EBF5;

          border-radius:
            18px;

          background:
            #FFFFFF;

          box-shadow:
            0 8px 28px
            rgba(
              10,
              35,
              84,
              0.05
            );

          overflow:
            hidden;
        }

        /* ==========================================
           HEADER
        ========================================== */

        .bs-events-header {
          display:
            flex;

          align-items:
            center;

          justify-content:
            space-between;

          gap:
            12px;

          padding:
            16px 18px 12px;
        }

        .bs-events-title-wrap {
          display:
            flex;

          align-items:
            center;

          gap:
            8px;
        }

        .bs-events-title-icon {
          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          width:
            30px;

          height:
            30px;

          flex-shrink:
            0;

          border-radius:
            9px;

          background:
            #EEF4FF;

          color:
            #2F6FE4;

          font-size:
            14px;
        }

        .bs-events-header h4 {
          margin:
            0;

          color:
            #0A2354;

          font-size:
            14px;

          font-weight:
            700;
        }

        .bs-events-seeall {
          display:
            inline-flex;

          align-items:
            center;

          gap:
            4px;

          flex-shrink:
            0;

          color:
            #2F6FE4;

          font-size:
            11.5px;

          font-weight:
            700;

          text-decoration:
            none;

          transition:
            0.15s ease;
        }

        .bs-events-seeall i {
          font-size:
            12px;

          transition:
            transform
            0.15s ease;
        }

        .bs-events-seeall:hover {
          color:
            #1447B0;
        }

        .bs-events-seeall:hover i {
          transform:
            translateX(2px);
        }

        /* ==========================================
           EMPTY STATE
        ========================================== */

        .bs-events-empty {
          display:
            flex;

          flex-direction:
            column;

          align-items:
            center;

          padding:
            8px 18px 22px;

          text-align:
            center;
        }

        .bs-events-empty-icon {
          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          width:
            48px;

          height:
            48px;

          margin-bottom:
            10px;

          border-radius:
            14px;

          background:
            linear-gradient(
              135deg,
              #EEF4FF,
              #F7F9FF
            );

          color:
            #2F6FE4;

          font-size:
            20px;

          box-shadow:
            inset 0 0 0 1px
            rgba(
              47,
              111,
              228,
              0.08
            );
        }

        .bs-events-empty h5 {
          margin:
            0;

          color:
            #0A2354;

          font-size:
            13px;

          font-weight:
            700;
        }

        .bs-events-empty p {
          max-width:
            210px;

          margin:
            5px 0 12px;

          color:
            #8A93A3;

          font-size:
            11px;

          line-height:
            1.5;
        }

        .bs-events-explore {
          display:
            inline-flex;

          align-items:
            center;

          justify-content:
            center;

          gap:
            5px;

          min-height:
            32px;

          padding:
            0 14px;

          border-radius:
            999px;

          background:
            #EEF4FF;

          color:
            #2F6FE4;

          font-size:
            11px;

          font-weight:
            700;

          text-decoration:
            none;

          transition:
            0.15s ease;
        }

        .bs-events-explore:hover {
          background:
            #2F6FE4;

          color:
            #FFFFFF;
        }

        /* ==========================================
           EVENT LIST
        ========================================== */

        .bs-events-list {
          padding:
            0 10px 12px;
        }

        .bs-events-item {
          display:
            flex;

          align-items:
            center;

          gap:
            10px;

          min-width:
            0;

          padding:
            10px;

          border-radius:
            14px;

          color:
            inherit;

          text-decoration:
            none;

          transition:
            0.15s ease;
        }

        .bs-events-item:hover {
          background:
            #F6F9FE;
        }

        .bs-events-date {
          display:
            flex;

          flex-direction:
            column;

          align-items:
            center;

          justify-content:
            center;

          width:
            48px;

          height:
            52px;

          flex-shrink:
            0;

          border:
            1px solid
            #DCE7FA;

          border-radius:
            13px;

          background:
            linear-gradient(
              180deg,
              #F5F8FF,
              #FFFFFF
            );

          color:
            #0A2354;

          box-shadow:
            0 4px 12px
            rgba(
              10,
              35,
              84,
              0.04
            );
        }

        .bs-events-month {
          color:
            #2F6FE4;

          font-size:
            9px;

          font-weight:
            800;

          letter-spacing:
            0.7px;
        }

        .bs-events-date strong {
          margin-top:
            1px;

          font-size:
            18px;

          line-height:
            1;

          font-weight:
            800;
        }

        .bs-events-info {
          flex:
            1;

          min-width:
            0;
        }

        .bs-events-topline {
          display:
            flex;

          align-items:
            center;

          gap:
            6px;

          min-width:
            0;
        }

        .bs-events-topline h5 {
          overflow:
            hidden;

          margin:
            0;

          color:
            #0A2354;

          font-size:
            12.5px;

          font-weight:
            700;

          white-space:
            nowrap;

          text-overflow:
            ellipsis;
        }

        .bs-events-category {
          flex-shrink:
            0;

          max-width:
            82px;

          overflow:
            hidden;

          padding:
            3px 7px;

          border-radius:
            999px;

          background:
            #EEF4FF;

          color:
            #2F6FE4;

          font-size:
            8.5px;

          font-weight:
            700;

          white-space:
            nowrap;

          text-overflow:
            ellipsis;
        }

        .bs-events-meta {
          display:
            flex;

          flex-direction:
            column;

          gap:
            2px;

          margin-top:
            5px;
        }

        .bs-events-meta span {
          display:
            flex;

          align-items:
            center;

          gap:
            4px;

          min-width:
            0;

          overflow:
            hidden;

          color:
            #8A93A3;

          font-size:
            9.8px;

          font-weight:
            500;

          white-space:
            nowrap;

          text-overflow:
            ellipsis;
        }

        .bs-events-meta i {
          flex-shrink:
            0;

          color:
            #A9B4C7;

          font-size:
            10px;
        }

        .bs-events-arrow {
          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          width:
            26px;

          height:
            26px;

          flex-shrink:
            0;

          border:
            1px solid
            #E1E8F3;

          border-radius:
            50%;

          color:
            #7890B4;

          font-size:
            11px;

          transition:
            0.15s ease;
        }

        .bs-events-item:hover
        .bs-events-arrow {
          border-color:
            #2F6FE4;

          background:
            #2F6FE4;

          color:
            #FFFFFF;
        }

        /* ==========================================
           LOADING SKELETON
        ========================================== */

        .bs-events-loading {
          padding:
            0 10px 12px;
        }

        .bs-events-skeleton-row {
          display:
            flex;

          align-items:
            center;

          gap:
            10px;

          padding:
            10px;
        }

        .bs-events-skeleton-date {
          width:
            48px;

          height:
            52px;

          flex-shrink:
            0;

          border-radius:
            13px;

          background:
            linear-gradient(
              90deg,
              #EDF2F9 25%,
              #F8FAFD 37%,
              #EDF2F9 63%
            );

          background-size:
            400% 100%;

          animation:
            bs-events-shimmer
            1.3s ease
            infinite;
        }

        .bs-events-skeleton-content {
          flex:
            1;
        }

        .bs-events-skeleton-line {
          height:
            8px;

          border-radius:
            999px;

          background:
            linear-gradient(
              90deg,
              #EDF2F9 25%,
              #F8FAFD 37%,
              #EDF2F9 63%
            );

          background-size:
            400% 100%;

          animation:
            bs-events-shimmer
            1.3s ease
            infinite;
        }

        .bs-events-skeleton-line--title {
          width:
            72%;
        }

        .bs-events-skeleton-line--small {
          width:
            44%;

          margin-top:
            8px;
        }

        @keyframes bs-events-shimmer {
          0% {
            background-position:
              100% 0;
          }

          100% {
            background-position:
              -100% 0;
          }
        }

        /* ==========================================
           RESPONSIVE
        ========================================== */

        @media (
          max-width:
            575px
        ) {
          .bs-events-header {
            padding:
              14px 14px 10px;
          }

          .bs-events-list,
          .bs-events-loading {
            padding-left:
              6px;

            padding-right:
              6px;
          }

          .bs-events-empty {
            padding-left:
              14px;

            padding-right:
              14px;
          }
        }
      `}</style>
    </div>
  );
};

export default Events;
